import { DiscountsPageContent } from './DiscountsLayoutPage';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { useDi } from '@root/Services/DI';
import { Box, Text, Group, Badge, useMantineTheme } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@root/Components/DataGrid';
import { DataColumnConfig } from '@root/Components/DataGrid/Models';
import { DiscountQueryService } from '@root/Services/Invoices/DiscountQueryService';
import { RangeSelection } from './DiscountsPage';
import { GridFlexCell } from '@root/Components/DataGrid/Design';
import { CustomColors } from '@root/Design/Themes';
import { FillerSwitch } from '@root/Design/Filler';
import { useEllipsisTextClass } from '@root/Design/Text';
import { MdsHistoricalRecordCommitmentTerm } from '@apis/Invoices/model';
import { addDays, addMonths, differenceInDays } from 'date-fns';

const getDiscountData = async (querySvc: DiscountQueryService, platform: string) => {
    const current = new Date();
    const fromMonth = addMonths(current, -1);
    const discountApi = querySvc.scopeApi(platform, fromMonth, current);
    const maxDate = await querySvc
        .queryBuilder()
        .where((b) => b.model.Date!.onOrBefore(current))
        .select((b) => ({ dt: b.max(b.model.Date!) }))
        .execute(discountApi)
        .then((r) => (r.Results?.[0]?.dt ? FormatService.instance.parseDateNoTime(r.Results?.[0]?.dt) : addDays(new Date(), -1)));
    const reportStartDt = addDays(maxDate, -30);

    const baseData = await querySvc
        .queryBuilder()
        .where((b) => b.and(b.model.Date.onOrAfter(reportStartDt), b.model.Date.before(maxDate)))
        .select((b) => ({
            CommitmentId: b.model.CommitmentId!,
            Platform: b.model.Platform,
            Name: b.model.DisplayName,
            AccountName: b.values(b.model.AccountName, null, 'Unknown'),
            AccountId: b.values(b.model.AccountId, null, ''),
            Type: b.values(b.model.CommitmentTerm, null, '') as unknown as MdsHistoricalRecordCommitmentTerm,
            PaymentOption: b.values(b.model.PaymentOption, null, ''),
            Expires: b.values(b.model.EndDate, null, ''),
            UnlockAt: b.max(b.model.UnlockAt),
            Ct: b.count(),
            CommitmentCost: b.sum(b.model.CommitmentCost),
            NetSavings: b.sum(b.model.NetSavings),
            MdsFees: b.sum(b.model.MdsFees),
            CoveredCost: b.sum(b.model.CoveredOnDemandCost),
            TotalRunningHrs: b.sum(b.model.RunningHrs),
            UtilizationPct: b.aggIf(b.model.RunningHrs!.ne(0), b.avg(b.model.UtilizationPct)),
        }))
        .execute((q) => querySvc.query(platform, q));

    return (baseData.Results ?? []).map((item) => {
        const { CommitmentCost, Ct, MdsFees, PaymentOption, Platform, Type, TotalRunningHrs } = item;
        const insured = item.Type === 'InsuranceBacked1Year' || item.Type === 'InsuranceBacked30Day';
        const term =
            Type === 'InsuranceBacked30Day' ? '30-Day' : Type === 'Standard3Year' ? '3 Years' : Type === 'Standard1Year' ? '1 Year' : 'Unknown';
        const expDt = item.Expires ? new Date(item.Expires) : null;
        const unlockAt = item.UnlockAt ? new Date(item.UnlockAt) : null;
        const expired = expDt && expDt < current;
        const expiring = expDt && Math.abs(differenceInDays(expDt, current)) <= 30;
        const status = expired ? 'Expired' : expiring ? 'Expiring' : 'Active';
        const paymentOption = insured ? 'Insured' : PaymentOption;
        const perHrCost = TotalRunningHrs <= 0 ? null : CommitmentCost / TotalRunningHrs;
        const cappedUtilPct = item.UtilizationPct > 1 ? 1 : item.UtilizationPct;

        return {
            ...item,
            ExpirationDt: expDt,
            DaysRemaining: Math.round(Math.max(0, expDt ? differenceInDays(expDt, current) : 0)),
            UnlockAt: unlockAt,
            PaymentOption: paymentOption,
            Rate: perHrCost,
            Status: status,
            CommitmentImg: insured
                ? '/assets/cloudsaver_icon.png'
                : Platform === 'Aws'
                ? '/assets/aws_resource_box.png'
                : item.Platform === 'Azure'
                ? '/assets/azure_arm.png'
                : null,
            PlatformImg: Platform === 'Aws' ? '/assets/Amazon_Web_Services_Logo.svg' : Platform === 'Azure' ? '/assets/azure_logo.svg' : null,
            Term: [term, paymentOption].filter((v) => !!v).join(' | '),
            UtilizationPct: cappedUtilPct,
        };
    });
};

type DiscountInventory = Awaited<ReturnType<typeof getDiscountData>>[number];

export function Inventory(props: { platform: string }) {
    const { platform } = props;
    const { colors } = useMantineTheme();
    const [data, setData] = useState<DiscountInventory[]>([]);
    const fmtSvc = useDi(FormatService);
    const discountQueryService = useDi(DiscountQueryService);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchDiscountData = async () => {
        try {
            setIsLoading(true);
            setData(await getDiscountData(discountQueryService, platform));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDiscountData();
    }, [platform]);

    const ellipsisCls = useEllipsisTextClass();
    const columns = useMemo(() => {
        if (!data.length) {
            return [];
        }

        const termFilterFields = ['Platform', 'Name', 'AccountName', 'AccountId', 'Term', 'Status'] as const;
        const items: Record<(typeof termFilterFields)[number], Set<string>> = data.reduce((acc, item) => {
            termFilterFields.forEach((field) => {
                const value = item[field] as string;
                if (value) {
                    acc[field] = acc[field] || new Set();
                    acc[field].add(value);
                }
            });
            return acc;
        }, {} as Record<(typeof termFilterFields)[number], Set<string>>);
        const termFilterOptions = termFilterFields.reduce(
            (result, field) =>
                result.set(
                    field,
                    Array.from(items[field])
                        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                        .map((v) => ({ value: v, label: v }))
                ),
            new Map<(typeof termFilterFields)[number], Array<{ value: string; label: string }>>()
        );
        const filterOnlyColumns = ['Platform', 'AccountName', 'AccountId'] as const;
        const filterCols = filterOnlyColumns.map((col) => ({
            accessor: col,
            id: col,
            header: col,
            defaultWidth: 200,
            type: 'string',
            sortField: col,
            hideInColumnSelector: true,
            filter: {
                name: col,
                filterType: 'string',
                filterField: col,
                options: {
                    getValueProvider: () => termFilterOptions.get(col) || [],
                },
            },
            defaultHidden: true,
        }));

        const result = [
            ...filterCols,
            {
                id: 'CommitmentId',
                header: 'Commitment Info',
                accessor: 'Name',
                defaultWidth: 300,
                type: 'string',
                filter: {
                    name: 'Name',
                    filterField: 'Name',
                    filterType: 'string',
                    options: {
                        getValueProvider: () => termFilterOptions.get('Name') || [],
                    },
                },
                cellRenderer: (item) => {
                    return (
                        <GridFlexCell>
                            <img src={item.PlatformImg || ''} style={{ width: '25px' }} />
                            <Box ml="xs" sx={{ overflow: 'hidden' }}>
                                <Text className={ellipsisCls} sx={{ flex: 1 }} size="sm" weight={600}>
                                    {item.Name}
                                </Text>
                                <Text className={ellipsisCls} sx={{ flex: 1 }} size="xs">
                                    {item.AccountName}:{' '}
                                    <Text span color="dimmed">
                                        {item.AccountId}
                                    </Text>
                                </Text>
                            </Box>
                        </GridFlexCell>
                    );
                },
            },
            {
                id: 'OfferingInfo',
                header: 'Offering Info',
                accessor: 'Term',
                defaultWidth: 200,
                type: 'string',
                cellRenderer: (item) => {
                    return (
                        <GridFlexCell>
                            <img src={item.CommitmentImg || ''} style={{ width: '25px' }} />
                            <Text className={ellipsisCls} sx={{ flex: 1 }} ml="xs" size="sm">
                                {item.Term}
                            </Text>
                        </GridFlexCell>
                    );
                },
                filter: {
                    name: 'Term',
                    filterType: 'string',
                    filterField: 'Term',
                    options: {
                        getValueProvider: () => termFilterOptions.get('Term') || [],
                    },
                },
            },
            {
                id: 'Amount',
                header: 'Amount',
                accessor: 'Rate',
                defaultWidth: 100,
                type: 'number',
                align: 'right',
                formatter: (item) => (!item.Rate ? '' : `${fmtSvc.formatMoney(item.Rate)}/hr`),
                filter: true,
            },
            {
                id: 'UtilizationPct',
                header: 'Utilization',
                accessor: 'UtilizationPct',
                defaultWidth: 100,
                type: 'number',
                align: 'right',
                formatter: (item) => (!item.UtilizationPct ? '' : `${fmtSvc.formatPercent(item.UtilizationPct)}`),
                filter: true,
            },
            {
                id: '30DayCost',
                header: '30-Day Cost',
                accessor: 'CommitmentCost',
                defaultWidth: 150,
                type: 'number',
                align: 'right',
                formatter: (item) => fmtSvc.formatMoney(item.CommitmentCost),
                filter: true,
            },
            {
                id: '30DaySavings',
                header: '30-Day Net Savings',
                accessor: 'NetSavings',
                defaultWidth: 170,
                type: 'number',
                align: 'right',
                cellRenderer: (item) => {
                    const value = item.NetSavings;
                    const color = value > 0 ? colors.success[5] : value < 0 ? colors.error[5] : undefined;
                    return (
                        <GridFlexCell align="right">
                            <Text color={color as unknown as CustomColors}>{fmtSvc.formatMoney(value)}</Text>
                        </GridFlexCell>
                    );
                },
                filter: true,
            },
            {
                id: 'Expires',
                header: 'Expires',
                accessor: 'DaysRemaining',
                defaultWidth: 100,
                type: 'number',
                align: 'right',
                cellRenderer: (item) => <CommitmentExpiryDays item={item} />,
                filter: true,
            },
            {
                id: 'Status',
                header: 'Status',
                accessor: 'Status',
                defaultWidth: 100,
                type: 'string',
                cellRenderer: (item) => <CommitmentStatus item={item} />,
                filter: {
                    name: 'Status',
                    filterType: 'string',
                    filterField: 'Status',
                    options: {
                        getValueProvider: () => termFilterOptions.get('Status') || [],
                    },
                },
            },
        ] as DataColumnConfig<DiscountInventory>[];

        result.forEach((col) => {
            col.noRemove = true;
        });

        return result;
    }, [data]);

    return (
        <DiscountsPageContent>
            <FillerSwitch loading={isLoading || !columns.length} noData={data.length === 0} noDataMessage="No Inventory Data Available">
                {() => (
                    <DataGrid
                        dataSource={data}
                        columns={columns}
                        itemHeight={50}
                        hideColumnSelector
                        allowNonColumnFilters
                        showRefresh
                        exportName="Discounts Inventory"
                        selectionMode="none"
                    />
                )}
            </FillerSwitch>
        </DiscountsPageContent>
    );
}

function CommitmentStatus({ item }: { item: DiscountInventory }) {
    const { Status } = item;
    const color = Status === 'Expired' ? 'gray' : Status === 'Expires Soon' ? 'warning' : 'primary';
    const status = Status === 'Expired' ? 'Expired' : Status === 'Expires Soon' ? 'Expiring' : 'Active';

    return (
        <GridFlexCell>
            <Badge color={color}>{status}</Badge>
        </GridFlexCell>
    );
}

function CommitmentExpiryDays({ item }: { item: DiscountInventory }) {
    const fmtSvc = useFmtSvc();
    const { DaysRemaining } = item;

    return (
        <GridFlexCell align="right">
            <Box>
                <Text align="right" size="sm">
                    {fmtSvc.formatInt0Dec(DaysRemaining)}
                </Text>
                <Text align="right" size="xs" color="dimmed">
                    days
                </Text>
            </Box>
        </GridFlexCell>
    );
}
